// ViewCustomer.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const Section = ({ title, children }) => {
    const [open, setOpen] = useState(true);
    return (
        <div className="mb-3 border">
            <div
                className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }}
                onClick={() => setOpen(!open)}
            >
                <span>{title}</span>
                <span>{open ? "▾" : "▸"}</span>
            </div>
            {open && <div className="row g-3 p-3">{children}</div>}
        </div>
    );
};

export default function ViewCustomer() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [customer, setCustomer] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [bankDetails, setBankDetails] = useState(null);
    const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "details");
    const [activeSubTab, setActiveSubTab] = useState("systemNotes");
    const [systemNotes, setSystemNotes] = useState([]);
    const [customerIds, setCustomerIds] = useState([]);
    const [navbarColor, setNavbarColor] = useState("#080235");

    const token = localStorage.getItem("token");

    // Bootstrap loader
    useEffect(() => {
        const bootstrapLink = document.createElement("link");
        bootstrapLink.rel = "stylesheet";
        bootstrapLink.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
        document.head.appendChild(bootstrapLink);
        const iconsLink = document.createElement("link");
        iconsLink.rel = "stylesheet";
        iconsLink.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
        document.head.appendChild(iconsLink);
        return () => { bootstrapLink.remove(); iconsLink.remove(); };
    }, []);

    // Auth check
    useEffect(() => {
        const roles = JSON.parse(localStorage.getItem("roles") || "[]");
        if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
        if (!roles.includes("ROLE_ADMIN") && !roles.includes("ROLE_CUSTOMER_MANAGER")) {
            toast.error("Unauthorized."); navigate("/"); return;
        }
    }, [navigate, token]);

    // Navbar color
    useEffect(() => {
        const userId = localStorage.getItem("userId");
        if (!token || !userId) return;
        fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.json())
            .then((d) => { if (d?.navbarColor) setNavbarColor(d.navbarColor); })
            .catch(() => { });
    }, [token]);

    // Load all customer IDs for navigation
    useEffect(() => {
        fetch(`${API_BASE_URL}/api/customers/all`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.ok ? r.json() : [])
            .then((data) => {
                const list = Array.isArray(data) ? data : data.content || [];
                const ids = list.map((c) => Number(c.id)).filter((i) => !isNaN(i)).sort((a, b) => a - b);
                setCustomerIds(ids);
            })
            .catch(() => { });
    }, [token]);

    // Load customer
    useEffect(() => {
        if (!id) return;
        fetch(`${API_BASE_URL}/api/customers/${id}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.json())
            .then((data) => setCustomer(data))
            .catch(() => toast.error("Failed to load customer."));
    }, [id, token]);

    // Load contacts
    useEffect(() => {
        if (!id) return;
        fetch(`${API_BASE_URL}/api/customers/${id}/contacts`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.ok ? r.json() : [])
            .then((data) => setContacts(data))
            .catch(() => setContacts([]));
    }, [id, token]);

    // Load system notes
    useEffect(() => {
        if (!id) return;
        fetch(`${API_BASE_URL}/api/audit/customer/${id}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.ok ? r.json() : [])
            .then((data) => setSystemNotes(data))
            .catch(() => setSystemNotes([]));
    }, [id, token]);

    const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB") : "—";
    const formatDateTime = (iso) => {
        if (!iso) return "";
        return new Date(iso).toLocaleString("en-IN", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    };
    const maskValue = (value, visible = 2) => {
        if (!value) return "—";
        const str = value.toString();
        if (str.length <= visible) return str;
        return "*".repeat(str.length - visible) + str.slice(-visible);
    };
    const initials = (name = "") => name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "??";

    const getStatusBadge = (status) => {
        const map = { Active: "success", Inactive: "secondary", Blacklisted: "danger" };
        return <span className={`badge bg-${map[status] || "secondary"}`}>{status || "—"}</span>;
    };

    if (!customer) return <div className="p-4">Loading...</div>;

    const c = customer;
    const addresses = c.addresses || [];

    return (
        <>
            <div className="fixed-top">
                <AdminNavbar />
            </div>
            <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

                {/* Header */}
                <div className="border-bottom pb-2 mb-3">
                    {/* Top Row */}
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center">
                            <i className="bi bi-building fs-3 me-2 text-primary"></i>
                            <h5 className="m-0 me-3">Customer</h5>
                        </div>
                        <div className="d-flex align-items-center text-muted">
                            <i
                                className="bi bi-arrow-left-circle me-3"
                                style={{ cursor: "pointer", fontSize: "1.3rem", color: "#0d6efd" }}
                                title="Previous Customer"
                                onClick={() => {
                                    const currentIndex = customerIds.indexOf(parseInt(id));
                                    if (currentIndex > 0) navigate(`/viewcustomer/${customerIds[currentIndex - 1]}`);
                                    else toast.info("This is the first customer.");
                                }}
                            />
                            <i
                                className="bi bi-arrow-right-circle me-3"
                                style={{ cursor: "pointer", fontSize: "1.3rem", color: "#0d6efd" }}
                                title="Next Customer"
                                onClick={() => {
                                    const currentIndex = customerIds.indexOf(parseInt(id));
                                    if (currentIndex < customerIds.length - 1) navigate(`/viewcustomer/${customerIds[currentIndex + 1]}`);
                                    else toast.info("This is the last customer.");
                                }}
                            />
                            <span className="me-2" style={{ cursor: "pointer", color: "blue" }} onClick={() => navigate("/allcustomers")}>
                                List
                            </span>
                        </div>
                    </div>

                    {/* Second Row: Avatar + Name + Badges */}
                    <div className="d-flex align-items-center mb-2">
                        <div style={{
                            width: 40, height: 40, borderRadius: 8, background: "#eaf0f8",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 14, fontWeight: 600, color: "#3a6090", marginRight: 12, flexShrink: 0,
                        }}>
                            {initials(c.customerName)}
                        </div>
                        <h4 className="m-0 me-3">{c.customerName}</h4>
                        {/* {c.customerCode && (
                            <span className="me-2" style={{ background: "#eaf0f8", color: "#3a6090", borderRadius: 4, padding: "2px 10px", fontFamily: "monospace", fontSize: 13 }}>
                                {c.customerCode}
                            </span>
                        )} */}
                        {c.status && c.status !== "Active" && getStatusBadge(c.status)}
                        {/* {c.customerType && (
                            <span className="ms-2 badge bg-light text-dark border">{c.customerType}</span>
                        )} */}
                    </div>

                    {/* Third Row: Action Buttons */}
                    <div className="d-flex align-items-center mb-2">
                        <button className="btn btn-primary me-2" onClick={() => navigate(`/editcustomer/${id}`)}>Edit</button>
                        <button className="btn btn-light btn-sm me-2" onClick={() => navigate(-1)}>Back</button>
                    </div>
                </div>

                {/* Primary Info */}
                <Section title="Basic Information">
                    <div className="col-md-3"><strong>Customer Code</strong><br /><span style={{ fontFamily: "monospace" }}>{c.customerCode || "—"}</span></div>
                    <div className="col-md-3"><strong>Customer Name</strong><br />{c.customerName}</div>
                    <div className="col-md-3"><strong>Customer Type</strong><br />{c.customerType || "—"}</div>
                    <div className="col-md-3"><strong>Industry</strong><br />{c.industry || "—"}</div>
                    <div className="col-md-3"><strong>Account Manager</strong><br />{c.accountManager || "—"}</div>
                    <div className="col-md-3">
                        <strong>Website</strong><br />
                        {c.website ? (
                            <a href={c.website} target="_blank" rel="noopener noreferrer" style={{ color: "#0052CC" }}>{c.website}</a>
                        ) : "—"}
                    </div>
                    <div className="col-md-3"><strong>Status</strong><br />{getStatusBadge(c.status)}</div>
                </Section>

                {/* Tabs */}
                <ul className="nav nav-tabs mb-2 fw-semibold" style={{ backgroundColor: navbarColor }}>
                    {[
                        ["details", "Address"],
                        ["contacts", "Contacts"],
                        ["billing", "Financial"],
                        ["notes", "Notes & Tags"],
                        ["system", "System Information"],
                    ].map(([tabId, label]) => (
                        <li className="nav-item" key={tabId}>
                            <button
                                type="button"
                                className={`nav-link ${activeTab === tabId ? "active" : ""}`}
                                onClick={() => setActiveTab(tabId)}
                                style={{
                                    color: activeTab === tabId ? navbarColor : "#fff",
                                    backgroundColor: activeTab === tabId ? "#fff" : "transparent",
                                    fontWeight: activeTab === tabId ? "600" : "normal",
                                }}
                            >
                                {label}
                                {tabId === "contacts" && contacts.length > 0 && (
                                    <span className="badge bg-secondary ms-1" style={{ fontSize: 10 }}>{contacts.length}</span>
                                )}
                            </button>
                        </li>
                    ))}
                </ul>

                <div className="border p-3">
                    {activeTab === "details" && (
                        <Section title="Address">
                            {addresses.length === 0 ? (
                                <div className="col-12 text-muted">No addresses added.</div>
                            ) : addresses.map((addr, idx) => (
                                <div key={idx} className="col-md-6 col-lg-4">
                                    <div className="border rounded p-3 h-100" style={{ backgroundColor: "#f8fafc" }}>
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <span className="badge bg-secondary">{addr.addressType || "Other"}</span>
                                            <span className="text-muted small">Address {idx + 1}</span>
                                        </div>
                                        <address className="mb-0" style={{ fontStyle: "normal", lineHeight: "1.8" }}>
                                            {addr.addressLine1 && <div>{addr.addressLine1}</div>}
                                            {addr.addressLine2 && <div>{addr.addressLine2}</div>}
                                            {(addr.city || addr.state || addr.postalCode) && (
                                                <div>
                                                    {[addr.city, addr.state, addr.postalCode].filter(Boolean).join(", ")}
                                                </div>
                                            )}
                                            {addr.country && (
                                                <div className="fw-semibold" style={{ color: "#3a6090" }}>{addr.country}</div>
                                            )}
                                        </address>
                                    </div>
                                </div>
                            ))}
                        </Section>
                    )}

                    {activeTab === "contacts" && (
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="fw-bold m-0">
                                    <i className="bi bi-people me-2"></i>Contacts ({contacts.length})
                                </h6>
                                {/* <button className="btn btn-sm btn-primary" onClick={() => navigate(`/addcontact?customerId=${id}`)}>
                                    <i className="bi bi-plus me-1"></i> Add Contact
                                </button> */}
                            </div>
                            {contacts.length > 0 ? (
                                <table className="table table-sm table-hover align-middle">
                                    <thead>
                                        <tr>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Name</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Email</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Phone</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Designation</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Department</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Primary</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {contacts.map((contact, i) => (
                                            <tr key={contact.id || i}>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div style={{ width: 30, height: 30, borderRadius: 6, background: "#eaf0f8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, color: "#3a6090", flexShrink: 0 }}>
                                                            {initials(contact.name)}
                                                        </div>
                                                        <span className="fw-semibold">{contact.name}</span>
                                                    </div>
                                                </td>
                                                <td>{contact.email ? <a href={`mailto:${contact.email}`} style={{ color: "#0052CC" }}>{contact.email}</a> : "—"}</td>
                                                <td>{contact.phone || "—"}</td>
                                                <td>{contact.designation || "—"}</td>
                                                <td>{contact.department || "—"}</td>
                                                <td>
                                                    {contact.primary
                                                        ? <span className="badge" style={{ background: "#1d4ed8", color: "#fff" }}>★ Yes</span>
                                                        : <span className="text-muted">—</span>}
                                                </td>
                                                <td>
                                                    <button className="btn btn-sm btn-outline-primary" onClick={() => navigate(`/viewcontact/${contact.id}`)}>View</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="text-muted">No contacts added yet.</p>
                            )}
                        </div>
                    )}

                    {activeTab === "billing" && (
                        <Section title="Financial">

                            <div className="col-md-3"><strong>Currency</strong><br /><span style={{ fontFamily: "monospace" }}>{c.currency || "—"}</span></div>
                            <div className="col-md-3"><strong>Payment Terms</strong><br />{c.paymentTerms || "—"}</div>
                            <div className="col-md-3"><strong>Tax Number / GST</strong><br /><span style={{ fontFamily: "monospace" }}>{c.taxNumber || "—"}</span></div>
                            {/* <div className="col-md-3"><strong>Created</strong><br />{formatDate(c.createdAt)}</div> */}
                        </Section>
                    )}

                    {activeTab === "notes" && (
                        <>
                            {c.tags && c.tags.length > 0 && (
                                <div className="mb-3">
                                    <strong>Tags</strong>
                                    <div className="mt-2 d-flex flex-wrap gap-2">
                                        {c.tags.map((t) => (
                                            <span key={t} style={{ background: "#eaf0f8", color: "#3a6090", border: "1px solid #d0dff0", borderRadius: 20, padding: "3px 12px", fontSize: 13 }}>
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {c.notes && (
                                <div>
                                    <strong>Notes</strong>
                                    <div className="mt-2 p-3 border rounded bg-light" style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{c.notes}</div>
                                </div>
                            )}
                            {!c.notes && (!c.tags || c.tags.length === 0) && (
                                <p className="text-muted">No notes or tags added.</p>
                            )}
                        </>
                    )}

                    {activeTab === "system" && (
                        <div className="p-3 border rounded bg-white">
                            <div className="mb-3">
                                <div className="form-check mb-2">
                                    <input type="checkbox" className="form-check-input" checked={c.status === "Inactive"} disabled readOnly />
                                    <label className="form-check-label fw-bold ms-2">INACTIVE</label>
                                </div>
                                <div className="text-muted small">
                                    <strong>Date Created:</strong> {formatDateTime(c.createdAt)}
                                </div>
                            </div>

                            <ul className="nav nav-tabs mb-3">
                                <li className="nav-item">
                                    <button className={`nav-link ${activeSubTab === "systemNotes" ? "active" : ""}`} onClick={() => setActiveSubTab("systemNotes")}>
                                        System Notes
                                    </button>
                                </li>
                            </ul>

                            {activeSubTab === "systemNotes" && (
                                <table className="table table-sm align-middle">
                                    <thead>
                                        <tr>
                                            <th className="text-start" style={{ backgroundColor: "#DFE4EB" }}>Date</th>
                                            <th className="text-start" style={{ backgroundColor: "#DFE4EB" }}>Set By</th>
                                            <th className="text-start" style={{ backgroundColor: "#DFE4EB" }}>Field</th>
                                            <th className="text-start" style={{ backgroundColor: "#DFE4EB" }}>Old Value</th>
                                            <th className="text-start" style={{ backgroundColor: "#DFE4EB" }}>New Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {systemNotes.length === 0 ? (
                                            <tr><td colSpan="5" className="text-muted text-center">No records to show.</td></tr>
                                        ) : (
                                            systemNotes.map((note, i) => (
                                                <tr key={i}>
                                                    <td className="text-start">{formatDateTime(note.modifiedDate)}</td>
                                                    <td className="text-start">{note.modifiedBy}</td>
                                                    <td className="text-start">{note.fieldName}</td>
                                                    <td className="text-start">{note.oldValue || "—"}</td>
                                                    <td className="text-start">{note.newValue || "—"}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}